// Security Module for Admin Panel
// Implements session timeout, rate limiting, and audit logging

import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

// ===== SESSION TIMEOUT CONFIGURATION =====
const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_TIME = 13 * 60 * 1000; // Show warning at 13 minutes
let sessionTimeout = null;
let warningTimeout = null;
let lastActivity = Date.now();

// ===== RATE LIMITING CONFIGURATION =====
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const loginAttempts = new Map(); // IP -> { count, lockedUntil, attempts[] }

// ===== SESSION TIMEOUT FUNCTIONS =====

export function initSessionTimeout(onWarning, onTimeout) {
  const resetTimers = () => {
    clearTimeout(sessionTimeout);
    clearTimeout(warningTimeout);
    lastActivity = Date.now();

    // Set warning timeout
    warningTimeout = setTimeout(() => {
      onWarning?.();
    }, WARNING_TIME);

    // Set logout timeout
    sessionTimeout = setTimeout(() => {
      onTimeout?.();
    }, SESSION_TIMEOUT);
  };

  // Track user activity
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  activityEvents.forEach(event => {
    document.addEventListener(event, resetTimers, { passive: true });
  });

  // Start initial timers
  resetTimers();

  return () => {
    // Cleanup function
    clearTimeout(sessionTimeout);
    clearTimeout(warningTimeout);
    activityEvents.forEach(event => {
      document.removeEventListener(event, resetTimers);
    });
  };
}

export function getSessionTimeRemaining() {
  return SESSION_TIMEOUT - (Date.now() - lastActivity);
}

// ===== RATE LIMITING FUNCTIONS =====

export function checkRateLimit(identifier = 'default') {
  const now = Date.now();
  const record = loginAttempts.get(identifier) || { count: 0, lockedUntil: 0, attempts: [] };

  // Check if still locked out
  if (record.lockedUntil > now) {
    const remainingMinutes = Math.ceil((record.lockedUntil - now) / 60000);
    return {
      allowed: false,
      reason: `Account locked. Try again in ${remainingMinutes} minute(s).`,
      retryAfter: record.lockedUntil
    };
  }

  // Reset if lockout expired
  if (record.lockedUntil > 0 && record.lockedUntil <= now) {
    record.count = 0;
    record.lockedUntil = 0;
    record.attempts = [];
  }

  // Check attempt count
  if (record.count >= MAX_LOGIN_ATTEMPTS) {
    record.lockedUntil = now + LOCKOUT_DURATION;
    loginAttempts.set(identifier, record);
    return {
      allowed: false,
      reason: `Too many failed attempts. Account locked for 15 minutes.`,
      retryAfter: record.lockedUntil
    };
  }

  return {
    allowed: true,
    remainingAttempts: MAX_LOGIN_ATTEMPTS - record.count
  };
}

export function recordLoginAttempt(identifier = 'default', success = false) {
  const now = Date.now();
  const record = loginAttempts.get(identifier) || { count: 0, lockedUntil: 0, attempts: [] };

  if (success) {
    // Reset on successful login
    loginAttempts.delete(identifier);
  } else {
    // Record failed attempt
    record.count++;
    record.attempts.push({ timestamp: now, success: false });
    loginAttempts.set(identifier, record);

    // Trigger lockout if max attempts reached
    if (record.count >= MAX_LOGIN_ATTEMPTS) {
      record.lockedUntil = now + LOCKOUT_DURATION;
    }
  }

  return {
    success,
    attemptCount: record.count,
    locked: record.lockedUntil > now
  };
}

export function getLoginAttemptInfo(identifier = 'default') {
  const record = loginAttempts.get(identifier);
  if (!record) return { count: 0, locked: false };

  const now = Date.now();
  return {
    count: record.count,
    locked: record.lockedUntil > now,
    lockedUntil: record.lockedUntil,
    remainingAttempts: Math.max(0, MAX_LOGIN_ATTEMPTS - record.count)
  };
}

// ===== AUDIT LOGGING FUNCTIONS =====

export async function logAdminAction(db, action, userId, details = {}) {
  try {
    // Get client info
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;

    // Try to get IP (note: this requires server-side for accuracy)
    let ipAddress = 'unknown';
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      ipAddress = ipData.ip;
    } catch (err) {
      console.warn('Could not fetch IP:', err);
    }

    const logData = {
      action,
      userId,
      timestamp: serverTimestamp(),
      userAgent,
      platform,
      language,
      ipAddress,
      details,
      sessionId: sessionStorage.getItem('adminSessionId') || 'unknown'
    };

    await addDoc(collection(db, 'adminAuditLogs'), logData);

    return { success: true };
  } catch (error) {
    console.error('Failed to log admin action:', error);
    return { success: false, error: error.message };
  }
}

export function logSecurityEvent(db, eventType, severity, message, metadata = {}) {
  return logAdminAction(db, 'SECURITY_EVENT', 'system', {
    eventType,
    severity,
    message,
    ...metadata
  });
}

// ===== IP DETECTION =====

export async function getClientIP() {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Failed to get client IP:', error);
    return 'unknown';
  }
}

export function getClientFingerprint() {
  // Create a simple browser fingerprint
  const components = [
    navigator.userAgent,
    navigator.language,
    window.screen.colorDepth,
    window.screen.width + 'x' + window.screen.height,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage
  ];

  // Simple hash function
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
}

// ===== SESSION MANAGEMENT =====

export function initializeAdminSession() {
  let sessionId = sessionStorage.getItem('adminSessionId');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('adminSessionId', sessionId);
    sessionStorage.setItem('sessionStartTime', Date.now().toString());
  }
  return sessionId;
}

export function destroyAdminSession() {
  sessionStorage.removeItem('adminSessionId');
  sessionStorage.removeItem('sessionStartTime');
  localStorage.removeItem('lastActivity');
}

export function getSessionDuration() {
  const startTime = parseInt(sessionStorage.getItem('sessionStartTime') || '0');
  if (!startTime) return 0;
  return Date.now() - startTime;
}

// ===== SECURITY CHECKS =====

export function validateSecureContext() {
  const checks = {
    https: window.location.protocol === 'https:',
    localStorage: typeof localStorage !== 'undefined',
    sessionStorage: typeof sessionStorage !== 'undefined',
    cookiesEnabled: navigator.cookieEnabled,
    modernBrowser: 'fetch' in window && 'Promise' in window
  };

  const allPassed = Object.values(checks).every(v => v);

  return {
    passed: allPassed,
    checks
  };
}

// ===== CONTENT SECURITY =====

export function sanitizeInput(input) {
  if (typeof input !== 'string') return input;

  // Remove dangerous characters and scripts
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .trim();
}

export function validateEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

export function validatePassword(password) {
  // Minimum 8 characters, at least one uppercase, one lowercase, one number
  const minLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return {
    valid: minLength && hasUpper && hasLower && hasNumber,
    checks: {
      minLength,
      hasUpper,
      hasLower,
      hasNumber,
      hasSpecial
    }
  };
}

// ===== EXPORT ALL =====

export const SecurityModule = {
  initSessionTimeout,
  getSessionTimeRemaining,
  checkRateLimit,
  recordLoginAttempt,
  getLoginAttemptInfo,
  logAdminAction,
  logSecurityEvent,
  getClientIP,
  getClientFingerprint,
  initializeAdminSession,
  destroyAdminSession,
  getSessionDuration,
  validateSecureContext,
  sanitizeInput,
  validateEmail,
  validatePassword
};

export default SecurityModule;
